import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/paywall');
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 5;

    const checkPaymentStatus = async () => {
      if (cancelled) return;
      try {
        const response = await api.get(`/payment/stripe/status/${sessionId}`);
        if (cancelled) return;

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Payment successful! Matches generated!');
          setTimeout(() => navigate('/dashboard'), 2000);
        } else if (attempts >= maxAttempts) {
          setStatus('timeout');
          toast.error('Payment verification timed out');
        } else {
          attempts += 1;
          setTimeout(checkPaymentStatus, 2000);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Payment check error:', error);
        setStatus('error');
        toast.error('Failed to verify payment');
      }
    };

    checkPaymentStatus();
    return () => { cancelled = true; };
  }, [searchParams, navigate]);

  return (
    <div className="app-container min-h-screen flex items-center justify-center px-4">
      <div className="text-center">
        {status === 'checking' && (
          <div data-testid="payment-status-checking">
            <Loader2 className="animate-spin text-primary mx-auto mb-4" size={64} />
            <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment</h2>
            <p className="text-gray-400">Finding your perfect blind matches... 💜</p>
          </div>
        )}
        
        {status === 'success' && (
          <div data-testid="payment-status-success">
            <CheckCircle className="text-green-500 mx-auto mb-4" size={64} />
            <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
            <p className="text-gray-400">Redirecting to your matches...</p>
          </div>
        )}
        
        {status === 'timeout' && (
          <div data-testid="payment-status-timeout">
            <h2 className="text-2xl font-bold text-white mb-2">Verification Timeout</h2>
            <p className="text-gray-400 mb-4">Please check your email or try refreshing</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-primary text-white rounded-full"
              data-testid="payment-timeout-dashboard-btn"
            >
              Go to Dashboard
            </button>
          </div>
        )}
        
        {status === 'error' && (
          <div data-testid="payment-status-error">
            <h2 className="text-2xl font-bold text-white mb-2">Verification Error</h2>
            <p className="text-gray-400 mb-4">Please contact support</p>
            <button
              onClick={() => navigate('/paywall')}
              className="px-6 py-3 bg-primary text-white rounded-full"
              data-testid="payment-error-retry-btn"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
